import { yupResolver } from '@hookform/resolvers/yup';
import { Col, Grid, message, Row, Spin, Table } from 'antd';
import {
  PermissionsEnum,
  useDeleteOfficeMutation,
  useGetOfficesQuery,
  useUpdateOfficeMutation,
} from 'gql/types';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getColumns } from './columns';
import { IEditOfficeFormSchema, editOfficeFormSchema } from './schema';
import styles from './styles.module.scss';

import CreateOfficeForm from '../CreateOfficeForm';

const OfficesTable: FC = () => {
  const { t } = useTranslation('user.LocationsTable');
  const { xs } = Grid.useBreakpoint();
  const [isMobile, setMobile] = useState<boolean>(false);
  const pagination = usePagination();
  const currentUser = useCurrentUser();
  const [officeName, setOfficeName] = useState<string | undefined>();
  const [idexxAccountNumber, setIdexxAccountNumber] = useState<
    string | undefined
  >();
  const [zoetisAccountNumber, setZoetisAccountNumber] = useState<
    string | undefined
  >();
  const [officeRegionId, setOfficeRegionId] = useState<string | undefined>();
  const createNewOfficeAllowed = currentUser.isCan(
    PermissionsEnum.CreteLocation
  );
  const deleteOfficeAllowed = currentUser.isCan(PermissionsEnum.DeleteLocation);
  const updateOfficeAllowed = currentUser.isCan(PermissionsEnum.UpdateLocation);

  useEffect(() => {
    xs !== undefined && setMobile(xs);
  }, [xs]);

  const [deleteOffice, deleteOfficeMutation] = useDeleteOfficeMutation({
    refetchQueries: ['getOffices'],
    awaitRefetchQueries: true,
    onCompleted: () => {
      message.success(t('officeDeleted'));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });
  const [updateOffice, updateOfficeMutation] = useUpdateOfficeMutation({
    refetchQueries: ['getOffices'],
    awaitRefetchQueries: true,
    onCompleted: () => {
      currentUser.refetchData();
      message.success(t('officeUpdated'));
      editOfficeForm.reset();
    },
    onError: (error) => {
      message.error(error.message);
    },
  });
  const editOfficeForm = useForm<IEditOfficeFormSchema>({
    resolver: yupResolver(editOfficeFormSchema),
    mode: 'onChange',
    defaultValues: {
      id: undefined,
      name: undefined,
      regionId: undefined,
    },
  });
  const editableKey = editOfficeForm.watch('id');
  const inputtedName = editOfficeForm.watch('name');
  const inputtedIdexxNumber = editOfficeForm.watch('idexxAccountNumber');
  const inputtedZoetisNumber = editOfficeForm.watch('zoetisAccountNumber');
  const inputtedRegionId = editOfficeForm.watch('regionId');

  const setEditableKey = (
    officeId?: string,
    currentOfficeName?: string,
    currentIdexxAccountNumber?: string,
    currentZoetisAccountNumber?: string,
    currentRegionId?: string
  ) => {
    editOfficeForm.reset();
    setOfficeName(currentOfficeName);
    setIdexxAccountNumber(currentIdexxAccountNumber);
    setZoetisAccountNumber(currentZoetisAccountNumber);
    setOfficeRegionId(currentRegionId);
    editOfficeForm.setValue('id', officeId as string, {
      shouldValidate: true,
    });
    if (currentRegionId) {
      editOfficeForm.setValue('regionId', currentRegionId, {
        shouldValidate: true,
      });
    }
  };
  const officesQuery = useGetOfficesQuery();
  const updateOfficeHandler = editOfficeForm.handleSubmit((formData) => {
    updateOffice({
      variables: {
        input: {
          ...formData,
        },
      },
    });
  });
  const deleteOfficeHandler = (id: string) => {
    deleteOffice({ variables: { input: { id } } });
  };
  const offices = officesQuery.data?.getOffices;

  const isLoading = [
    deleteOfficeMutation,
    updateOfficeMutation,
    officesQuery,
  ].some(({ loading }) => loading);

  const isNewValues =
    officeName !== inputtedName ||
    idexxAccountNumber !== inputtedIdexxNumber ||
    zoetisAccountNumber !== inputtedZoetisNumber ||
    officeRegionId !== inputtedRegionId;

  const savingIsDisabled =
    !editOfficeForm.formState.isValid ||
    !editOfficeForm.formState.isDirty ||
    !isNewValues;

  const officeTableColumns = useMemo(
    () =>
      getColumns({
        control: editOfficeForm.control,
        setEditableKey,
        editableKey,
        onSaveChanges: updateOfficeHandler,
        savingIsDisabled,
        onDelete: deleteOfficeHandler,
        isAllowedToDelete: deleteOfficeAllowed,
        isAllowedToEdit: updateOfficeAllowed,
        errors: editOfficeForm.formState.errors,
        isMobile,
      }),
    [
      editOfficeForm.control,
      inputtedName,
      inputtedRegionId,
      editableKey,
      editOfficeForm.formState.isValid,
      editOfficeForm.formState.isDirty,
      isMobile,
    ]
  );

  return (
    <Row gutter={[0, 20]}>
      {createNewOfficeAllowed && (
        <Col span={24}>
          <CreateOfficeForm />
        </Col>
      )}
      <Col span={24} className={styles.tableWrapper}>
        <Spin spinning={isLoading}>
          <Table
            rowKey={({ id }) => id}
            columns={officeTableColumns}
            dataSource={offices}
            pagination={pagination.config}
            size="small"
          />
        </Spin>
      </Col>
    </Row>
  );
};

export default OfficesTable;
